<script setup lang="ts">
import {
  RestaurantAreas,
  RestaurantLiteWithDistanceList,
  SelectedLocationWithCoords,
  RestaurantLiteWithDistance,
} from '~/lib/interfaces/restaurant'
import { isAutocompletePrediction, isGeocoderResult } from '~/lib/interfaces/helpers'
import { Coords } from '~/lib/models/geolocation'
import { distanceBetweenCoordinates } from '~/lib/geoUtils'

// Default zoom
const DEFAULT_ZOOM = 13

const props = defineProps<{
  centerMap?: Coords
  selectedLocation?: SelectedLocationWithCoords
  restaurantList?: RestaurantLiteWithDistanceList
  insideBounds?: {
    value: boolean
    setInsideBounds: (value: boolean) => void
  }
}>()

const { t } = useLocale()
const restaurantActions = useRestaurantActions()
const restaurantStore = useRestaurantStore()
const appStore = useAppStore()
const { trackSelectRestaurant, trackOnCloseModal } = useGTM()
const { isLupapMaps } = useLupap()
const localization = useConfiguration('localization')

const isMobile = inject<Ref<boolean>>('isMobile')
const showDirection = ref(!!props.selectedLocation)

const lat = restaurantStore?.provisionalAddress?.latitude ?? localization?.latitude ?? 0
const lng = restaurantStore?.provisionalAddress?.longitude ?? localization?.longitude ?? 0
const openedMarkerID = ref<string | null>(null)
const clickedRestaurantDetail = ref<RestaurantLiteWithDistance>()
const userLocation = ref<Coords>()

const center = computed(() => {
  if (restaurantStore.selectedGoogleLocation) {
    return {
      lat: restaurantStore.selectedGoogleLocation.lat,
      lng: restaurantStore.selectedGoogleLocation.lng,
    }
  } else if (props.centerMap) {
    return props.centerMap
  } else if (userLocation.value) {
    return userLocation.value
  } else {
    return {
      lat,
      lng,
    }
  }
})

// Take pinCenter from center computed
const pinCenter = ref({ lat: center.value.lat, lng: center.value.lng })

defineExpose({
  pinCenter,
})

const restaurants = computed(() => props?.restaurantList)
const allowPinDrag = computed(() => {
  return appStore.ecommerceConfig?.allowPinDrag || isLupapMaps.value
})

const circleBounds = await getCircleBounds({
  center: center.value,
  radius: 1000,
})

const openMarker = (id: string | null) => {
  if (!id || openedMarkerID.value) {
    openedMarkerID.value = null
    if (!id) return
  }
  showDirection.value = false
  openedMarkerID.value = id
  const clickedRestarurant = restaurants.value?.find(restaurant => restaurant.id === id)
  if (clickedRestarurant) clickedRestaurantDetail.value = clickedRestarurant
}

const description = computed(() => {
  if (!props.selectedLocation) return ''
  if (isAutocompletePrediction(props.selectedLocation, 'description')) {
    return props.selectedLocation.description
  }

  if (isGeocoderResult(props.selectedLocation, 'formatted_address')) {
    return props.selectedLocation.formatted_address
  }

  return ''
})

const isDLV = computed(() => restaurantStore.candidateArea === RestaurantAreas.DLV)

const selectRestaurant = async (restaurant: RestaurantLiteWithDistance) => {
  trackSelectRestaurant(restaurant.code)
  await restaurantStore.getRestaurantByCode({
    code: restaurant.code,
    area: RestaurantAreas.MOP,
  })

  await restaurantActions.setRestaurant(restaurantStore.restaurant!)
  trackOnCloseModal()
}

const controlPoiToShow = (isSelected: boolean, restaurantCode: string) => {
  const { isFavorite } = useFavoriteRestaurant(restaurantCode)

  if (isFavorite.value) {
    return isSelected ? '/images/icons/favPoiSelected.svg' : '/images/icons/favPoiWhite.svg'
  }

  return isSelected ? '/images/icons/basicPoiSelected.svg' : '/images/icons/basicPoiWhite.svg'
}

const getClosestRestaurant = () => {
  if (!restaurants.value) return undefined

  let minDistance = Infinity
  let closestRestaurant = restaurants.value[0]

  restaurants.value.forEach(restaurant => {
    const distance = isCypressSpecialBehaviour()
      ? distanceBetweenCoordinates(
          center.value.lat,
          center.value.lng,
          restaurant.coordinates.latitude,
          restaurant.coordinates.longitude
        )
      : window.google.maps.geometry.spherical.computeDistanceBetween(center.value, {
          lat: restaurant.coordinates.latitude,
          lng: restaurant.coordinates.longitude,
        })
    if (distance < minDistance) {
      minDistance = distance
      closestRestaurant = restaurant
    }
  })

  return closestRestaurant
}

const calculateZoomBasedOnDistance = () => {
  // Define rangos de distancia y niveles de zoom correspondientes
  const zoomLevels = [
    { minDistance: 10000, maxDistance: Infinity, zoomLevel: 5 },
    { minDistance: 5000, maxDistance: 9999, zoomLevel: 11 },
    { minDistance: 1250, maxDistance: 4999, zoomLevel: 13 },
    { minDistance: 625, maxDistance: 1249, zoomLevel: 14 },
    { minDistance: 313, maxDistance: 624, zoomLevel: 15 },
    { minDistance: 156, maxDistance: 312, zoomLevel: 16 },
    { minDistance: 78, maxDistance: 155, zoomLevel: 17 },
    { minDistance: 39, maxDistance: 77, zoomLevel: 19 },
  ]

  const closestRestaurant = getClosestRestaurant()

  if (!closestRestaurant) return DEFAULT_ZOOM

  const distance = isCypressSpecialBehaviour()
    ? distanceBetweenCoordinates(
        center.value.lat,
        center.value.lng,
        closestRestaurant.coordinates.latitude,
        closestRestaurant.coordinates.longitude
      )
    : window.google.maps.geometry.spherical.computeDistanceBetween(center.value, {
        lat: closestRestaurant.coordinates.latitude,
        lng: closestRestaurant.coordinates.longitude,
      })

  let zoomLevel = DEFAULT_ZOOM
  for (let i = 0; i < zoomLevels.length; i++) {
    if (distance >= zoomLevels[i].minDistance && distance <= zoomLevels[i].maxDistance) {
      zoomLevel = zoomLevels[i].zoomLevel
      break
    }
  }

  return zoomLevel
}

const toggleShowDirection = () => {
  if (!props.selectedLocation) return

  showDirection.value = true
}

watch(pinCenter, newCenter => {
  if (!props.insideBounds) return
  const isInisideBounds = checkIfInBounds({
    bounds: circleBounds,
    point: newCenter,
  })
  props.insideBounds.setInsideBounds(isInisideBounds)
})

watch(showDirection, () => {
  if (!showDirection.value) return
  setTimeout(() => {
    showDirection.value = false
  }, 5000)
})

onMounted(async () => {
  setTimeout(() => {
    showDirection.value = false
  }, 5000)
  const permissionStatus = await getPermissionStatus('geolocation')
  if (permissionStatus !== 'granted') return
  userLocation.value = await getUserLocation()
})
</script>

<template>
  <GMapMap
    :options="{
      zoomControl: false,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      disableDefaultUi: true,
      styles: MAP_STANDARD_GREY,
    }"
    :center="center"
    :zoom="calculateZoomBasedOnDistance()"
    class="h-full w-full"
    :class="`vue-map-container-black vue-map-container-black-a relative`"
  >
    <!-- Pin position of search -->
    <GMapMarker
      v-if="!isDLV || (isDLV && !allowPinDrag)"
      :position="center"
      :clickable="true"
      :draggable="false"
      :icon="'/images/icons/pinMap.svg'"
      @click="toggleShowDirection"
    >
      <GMapInfoWindow :opened="showDirection">
        <div class="flex flex-col gap-2 text-white">
          <div class="flex gap-2">
            <p class="text-sm font-bold">
              {{ description }}
            </p>
          </div>
        </div>
      </GMapInfoWindow>
    </GMapMarker>

    <!-- Pin position of userLocation (if exists) -->
    <GMapMarker
      v-if="userLocation"
      :position="userLocation"
      :clickable="false"
      :draggable="false"
      :icon="'/images/icons/pinMapLocation.svg'"
    ></GMapMarker>

    <GMapMarker
      v-for="restaurant in restaurants"
      :key="restaurant.id"
      :position="{
        lat: restaurant.coordinates.latitude,
        lng: restaurant.coordinates.longitude,
      }"
      :icon="controlPoiToShow(openedMarkerID === restaurant.id, restaurant.code)"
      :clickable="true"
      @click="openMarker(restaurant.id)"
    >
      <!-- Restaurant detail openen on desktop -->
      <div
        v-if="!isMobile && openedMarkerID === restaurant.id"
        id="mapFooter"
        class="absolute bottom-0 right-0 z-10 mb-9 mr-4 h-32 w-80"
      >
        <LazyRestaurantListItem
          :restaurant="clickedRestaurantDetail"
          :small-card="true"
          @select-restaurant="selectRestaurant"
        />
      </div>
    </GMapMarker>

    <template v-if="isDLV && selectedLocation">
      <template v-if="allowPinDrag">
        <GMapMarker
          :position="pinCenter"
          :clickable="false"
          :draggable="true"
          :icon="insideBounds?.value ? '/images/icons/pinDLV.svg' : '/images/icons/pinDLVout.svg'"
          @dragend="pinCenter = $event.latLng.toJSON()"
        >
          <GMapInfoWindow :opened="true">
            <div
              :class="insideBounds?.value ? 'text-white' : 'text-mcd-red'"
              class="flex flex-col gap-2"
            >
              <div class="flex gap-2">
                <img v-if="!insideBounds?.value" src="/images/icons/errorDLVmap.svg" alt="error" />
                <p class="text-sm font-bold">
                  {{ insideBounds?.value ? description : 'Fuera del área de ajuste' }}
                </p>
              </div>
              <p class="text-[10px]">{{ t('selectRestaurant.map.adjustPin') }}</p>
            </div>
          </GMapInfoWindow>
        </GMapMarker>
        <GMapCircle
          :options="{
            strokeColor: '#292929',
            strokeOpacity: 1,
            strokeWeight: 0,
            fillColor: '#292929',
            fillOpacity: 0.15,
          }"
          :radius="1000"
          :center="center"
        />
      </template>
    </template>
    <!-- Restaurant detail openen on mobile -->
    <!-- TODO this for desktop -->
    <div
      v-if="isMobile && openedMarkerID && clickedRestaurantDetail"
      id="mapFooter"
      class="fixed bottom-0 z-10 w-full"
    >
      <LazyRestaurantListItem
        :restaurant="clickedRestaurantDetail"
        :small-card="true"
        @select-restaurant="selectRestaurant"
      />
    </div>
  </GMapMap>
</template>
